import { useEffect } from "react";

/**
 * Work on the page that a reload would lose: a surface holds it (`holdUnsaved`) while its draft
 * differs from what is saved, and lets go (the returned release) once it is saved or discarded.
 * The release watch (release-watch.ts) reads it: while any work is held, the automatic reload
 * after a newer release waits and the shell offers the reload instead; once the last hold lets
 * go, the waiting reload runs.
 *
 * Keys only name the work (for a reader of the code); two holds under one key are two holds.
 */
export class UnsavedWork {
  private readonly holds = new Map<symbol, string>();
  private readonly listeners = new Set<() => void>();

  /** Holds work under `key` until the returned function is called; a second call does nothing. */
  hold(key: string): () => void {
    const token = Symbol(key);
    this.holds.set(token, key);
    this.notify();
    return () => {
      if (this.holds.delete(token)) this.notify();
    };
  }

  /** Whether any work is held. */
  has(): boolean {
    return this.holds.size > 0;
  }

  /** Called after every hold and release. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

/** The page's one registry: the Builder's surfaces hold work in it and the release watch reads it. */
export const unsavedWork = new UnsavedWork();

/** Holds work under `key` in the page's registry; the returned function lets go. */
export function holdUnsaved(key: string): () => void {
  return unsavedWork.hold(key);
}

/** Whether anything on the page holds unsaved work. */
export function hasUnsavedWork(): boolean {
  return unsavedWork.has();
}

/** Holds work under `key` while `unsaved` is true and the component is mounted. */
export function useUnsavedWork(
  key: string,
  unsaved: boolean,
  registry: UnsavedWork = unsavedWork,
): void {
  useEffect(() => (unsaved ? registry.hold(key) : undefined), [key, unsaved, registry]);
}
